import React, { useState } from 'react';
import { useSearch } from '../../Search';
import { SEARCH_TYPES, getTypeChildren } from '../../../../lib/search';
import SearchCountryFilter from '../SearchCountryFilter/SearchCountryFilter';
import SearchDateFilter from '../SearchDateFilter/SearchDateFilter';
import css from './SearchFilters.module.css';
import Total from '../../../../components/Total/Total';
import SearchIndustryFilterWrapper from '../SearchIndustryFilterWrapper/SearchIndustryFilterWrapper';

const CollapseIcon = ({ className, status }) => (
  <svg className={className} fill="none" viewBox="0 0 7 7" data-status={status}>
    <path d="M4 1L1 4L4 7" stroke="#131616" strokeWidth="1" />
  </svg>
);

const SearchFilterTypeLabel = ({ label }) => {
  if (label === 'actors') {
    return (
      <span className={css.pill} data-type="Actor">
        Actors
      </span>
    );
  }

  if (label === 'people') return 'People';
  if (label === 'peopleRegulator') return 'Regulators';
  if (label === 'government') return 'Governments';
  if (label === 'governmentRegulator') return 'Regulators';
  if (label === 'companies') return 'Companies';
  if (label === 'organization') return 'Organizations';
  return label;
};

const SearchFilterCollapse = ({ type, level, isDisabled }) => {
  const { searchTypes, setSearchTypes, counts } = useSearch();

  const [isOpen, setOpen] = useState(false);

  const typeTotal = counts ? counts[type] : 0;

  const isActive = searchTypes.some((s) => s === type);

  const handleTypeClick = () => {
    setSearchTypes([type]);
  };

  const handleToggleClick = (event) => {
    event.stopPropagation();
    setOpen((o) => !o);
  };

  const subtypes = getTypeChildren(type);

  return (
    <div
      className={css.collapse}
      data-enabled={isDisabled ? 'disabled' : 'enabled'}
      data-status={isActive ? 'selected' : 'unselected'}
      data-expanded={isOpen ? 'open' : 'closed'}
    >
      <div
        data-cy="search-filter-group"
        data-cy-name={type}
        data-level={level}
        onClick={handleTypeClick}
        className={css.collapseHeader}
      >
        <div className={css.collapseContent}>
          <p className={css.collapseText}>
            <SearchFilterTypeLabel label={type} />
          </p>
          <div className={css.collapseInfo}>
            <Total total={typeTotal} isLoading={isDisabled} />
            <div
              data-cy="search-filter-group-expand"
              className={css.collapseIcon}
              onClick={handleToggleClick}
            >
              <CollapseIcon
                className={css.collapseIconArrow}
                status={isOpen ? 'open' : 'closed'}
              />
            </div>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className={css.collapseBody} data-cy="search-filter-collapse-body">
          {subtypes.map((subtype) => (
            <SearchFilterGroup key={subtype} type={subtype} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

const SearchFilterType = ({
  label,
  level,
  total = 0,
  isActive = false,
  isDisabled = false,
  onClick,
}) => {
  return (
    <div
      data-cy="search-filter-type"
      data-cy-name={label}
      data-level={level}
      className={css.type}
      data-enabled={isDisabled ? 'disabled' : 'enabled'}
      data-status={isActive ? 'selected' : 'unselected'}
      onClick={onClick}
    >
      <p className={css.typeName}>
        <SearchFilterTypeLabel label={label} />
      </p>
      <Total
        data-cy="search-filter-type-total"
        isLoading={isDisabled}
        total={total}
      />
    </div>
  );
};

const SearchFilterGroup = ({ type, level }) => {
  const { searchTypes, setSearchTypes, isCountLoading, counts } = useSearch();

  const hasChildren = getTypeChildren(type).length > 0;

  if (hasChildren) {
    return (
      <SearchFilterCollapse
        type={type}
        level={level}
        isDisabled={isCountLoading}
      />
    );
  }

  const handleClick = () => {
    setSearchTypes([type]);
  };

  const total = counts ? counts[type] : 0;

  return (
    <SearchFilterType
      label={type}
      level={level}
      isActive={searchTypes.some((s) => s === type)}
      isDisabled={isCountLoading}
      total={total}
      onClick={handleClick}
    />
  );
};

const SearchFilters = () => {
  const { searchTypes, setSearchTypes } = useSearch();

  return (
    <div className={css.main}>
      <div className={css.types}>
        <SearchFilterType
          label="All results"
          total={0}
          isActive={searchTypes.length === 0}
          onClick={() => setSearchTypes([])}
          level={0}
        />
        {SEARCH_TYPES.map((t) => t.type).map((type) => (
          <SearchFilterGroup key={type} type={type} level={1} />
        ))}
      </div>
      <SearchDateFilter />
      <SearchIndustryFilterWrapper />
      <SearchCountryFilter />
    </div>
  );
};

export default SearchFilters;
